import { inject, injectable } from "@launchtray/tsyringe-async";
import { IMyWalletController } from "./IMyWalletController";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { IContextResolver } from "../../../../composition/IContextResolver";
import { SceneManager } from "../../../../../scene/SceneManager";
import { NFT } from "by/gamefactory/web3/nfts/NFT";
import { NFTPackage } from "by/gamefactory/web3/nfts/NFTPackage";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { GitBookPage, Redirector } from "../../../../../tools/Redirector";
import { RewardDto } from "../../../../networking/data/responses/RewardDto";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { IMyWalletModel } from "../model/IMyWalletModel";
import { ConnectedWalletType } from "../components/wallets/ConnectWalletView";
import { ExternalWalletType } from "../../../../../web3/externalWallets/ExternalWalletType";
import { AcceptOperationNotification } from "../../../common/components/AcceptOperationNotification";
import { SignMessageScene } from "../signMessage/SignMessageScene";
import { WalletDisconnectionPopupScene } from "../scenes/WalletDisconnectionPopupScene";
import { IInventory } from "../../../../../web3/models/IInventory";
import { WalletConnectMessagesScene } from "../walletConnectMessages/WalletConnectMessagesScene";
import { WalletConnectState } from "../walletConnectMessages/WalletConnectState";
import { BuildingRightsType } from "../../../../networking/data/responses/user/BuildingRightsType";
import { Ticker, utils } from "pixi.js";
import { OperationResult } from "../../defaultmessage/OperationResult";
import { DefaultMessagesSceneFactory } from "../../../SceneFactories";
import { TickerDelay } from "../../../../../tools/TickerDelay";
import { Chain } from "thirdweb/chains";

type SignMessageSceneFactory = (address: string, walletType: ExternalWalletType) => Promise<SignMessageScene>;
type WalletConnectMessagesSceneFactory = (state: WalletConnectState, walletType?: ExternalWalletType) => Promise<WalletConnectMessagesScene>;

@injectable()
export class MyWalletController implements IMyWalletController {
    private readonly ignoredAddresses: Array<string>;

    public constructor(
        @inject("IContextResolver")
        private readonly contextResolver: IContextResolver,
        private readonly sceneManager: SceneManager,
        @inject("IMyWalletModel")
        private readonly myWalletModel: IMyWalletModel,
        private readonly web3Model: Web3Model,
        @inject("IGuestModel")
        private readonly guestModel: IGuestModel,
        @inject("IPlayerModel")
        private readonly playerModel: IPlayerModel,
        @inject("IInventory")
        private readonly inventory: IInventory,
        private readonly asyncOperationNotification: AsyncOperationNotification,
        private readonly acceptOperationNotification: AcceptOperationNotification,
        @inject("ILogger")
        private readonly logger: ILogger
    ) {
        this.ignoredAddresses = [];
    }

    public async connectWallet(connectedWallet: ConnectedWalletType): Promise<void> {
        try {
            const initialRights = this.playerModel.buildingRights.value;
            const type = this.walletTypeToExternalWalletType(connectedWallet);
            let addresses: Array<string> = null;
            if (!utils.isMobile.any && window.ethereum == null) {
                addresses = await this.web3Model.connectExternalWallet(type);
            }
            else {
                addresses = await this.acceptOperationNotification.autoShowHide(this.web3Model.connectExternalWallet(type), type);
            }

            if (addresses == null) {
                this.logger.debug("No addresses after connect external wallet, nothing to link");
                return;
            }

            const availableWallets = addresses.filter(
                (availableAddress) => {
                    return !this.ignoredAddresses.includes(availableAddress) &&
                        (this.myWalletModel.wallets.value?.length > 0 ? this.myWalletModel.wallets.value?.findIndex((connectedWallet) => connectedWallet.walletAddress === availableAddress) === -1 : true)
                }
            );

            if (availableWallets.length === 0) {
                await this.showWalletConnectResultView(WalletConnectState.ALREADY_CONNECTED);
                return;
            }
            const address = availableWallets[0];
            this.asyncOperationNotification.show();
            const factory: SignMessageSceneFactory = await this.contextResolver.resolve("SignMessageSceneFactory");
            const scene = await factory(address, type);
            this.asyncOperationNotification.hide();
            const signature = await this.sceneManager.showViewableAsync(scene);
            if (signature == null || signature.length === 0) {
                this.logger.debug("Signature is empty, operation rejected");
                return;
            }
            if (await this.asyncOperationNotification.autoShowHide(this.myWalletModel.linkExternalWallet(address, signature, type))) {
                await this.showWalletConnectResultView(WalletConnectState.CONNECTED, type);
                this.asyncOperationNotification.show();
                this.web3Model.addExternalWallet(address);
                await this.inventory.update();
                await this.playerModel.loadPlayerData();
                await this.playerModel.loadBuildings(false);
                this.asyncOperationNotification.hide();
                if (initialRights === BuildingRightsType.RENTER && this.playerModel.buildingRights.value === BuildingRightsType.OWNER) {
                    location.reload();
                }
            }
            else {
                if (!this.ignoredAddresses.includes(address)) {
                    this.ignoredAddresses.push(address);
                }
                await this.showWalletConnectResultView(WalletConnectState.ALREADY_CONNECTED);
            }
        }
        catch (e: unknown) {
            this.logger.error("Exception on connect wallet: ", e);
        }
        finally {
            this.asyncOperationNotification.hide();
        }
    }

    public async disconnectWallet(address: string): Promise<boolean> {
        if (await this.showWalletDisconnectPopup()) {
            if (await this.asyncOperationNotification.autoShowHide(this.myWalletModel.unlinkExternalWallet(address))) {
                this.web3Model.removeExternalWallet(address);
                const addressIndex = this.ignoredAddresses.indexOf(address);
                if (addressIndex !== -1) {
                    this.ignoredAddresses.splice(addressIndex, 1);
                }
                return true;
            }
        }
        return false;
    }

    public async burnNFT(nft: NFT): Promise<RewardDto> {
        try {
            this.asyncOperationNotification.show();
            if (nft instanceof NFTPackage) {
                return await this.myWalletModel.burnPackage(nft);
            }
            this.logger.error(`Attemp of burn non NFTPackage, nft data:\n${nft.toString()}`);
            return null;
        }
        catch (e: unknown) {
            this.logger.error(`Exception on burn nft: ${nft}`, e);
            return null;
        }
        finally {
            this.asyncOperationNotification.hide();
        }
    }

    public updateDiceRollsBalance(rollsCount: number): void {
        this.playerModel.addRolls(rollsCount);
    }

    public updateCoinsBalance(coins: number): void {
        this.playerModel.addCoins(coins);
    }

    public updateShieldsCount(shieldsCount: number): void {
        this.playerModel.addShields(shieldsCount);
    }

    private async showWalletDisconnectPopup(): Promise<boolean> {
        try {
            return await this.sceneManager.showViewableAsync(await this.contextResolver.resolve(WalletDisconnectionPopupScene));
        }
        catch (e: unknown) {
            this.logger.error(e);
            return false;
        }
    }

    private async showWalletConnectResultView(state: WalletConnectState, walletType?: ExternalWalletType): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            const factory: WalletConnectMessagesSceneFactory = await this.contextResolver.resolve("WalletConnectMessagesSceneFactory");
            const scene = await factory(state, walletType);
            this.asyncOperationNotification.hide();
            await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error(e);
        }
    }

    public openIntercom(): void {
        Redirector.openIntercom(this.web3Model.email, Date.now(), this.guestModel.name.value, this.guestModel.id.value);
    }

    public openExportPrivateKeyPage(): void {
        Redirector.openGitBook(GitBookPage.EXPORT_WALLET_PRIVATE_KEY);
    }

    public async sendL1Erc20Tokens(erc20Address: string, address: string, value: number): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            const result = await this.web3Model.sendL1Erc20Tokens(erc20Address, address, value);
            //todo delay for correct update data after send tokens
            await TickerDelay.delay(1000);
            if (result) {
                await this.web3Model.updateMONBalance();
            }
            this.asyncOperationNotification.hide();
            await this.showDefaultMessage(result ? Promise.resolve(OperationResult.SUCCESS) : Promise.resolve(OperationResult.FAILURE));
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on sending Mon tokens: ${e}`);
            this.asyncOperationNotification.hide();
            await this.showDefaultMessage(Promise.resolve(OperationResult.FAILURE));
        }
    }

    public async sendErc20Tokens(chain: Chain, erc20Address: string, address: string, value: number): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            const result = await this.web3Model.sendErc20Tokens(chain, erc20Address, address, value);
            //todo delay for correct update data after send tokens
            await TickerDelay.delay(1000);
            if (result) {
                await this.web3Model.updateErc20Balance(chain, erc20Address)
            }
            this.asyncOperationNotification.hide();
            await this.showDefaultMessage(result ? Promise.resolve(OperationResult.SUCCESS) : Promise.resolve(OperationResult.FAILURE));
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on sending Mon tokens: ${e}`);
            this.asyncOperationNotification.hide();
            await this.showDefaultMessage(Promise.resolve(OperationResult.FAILURE));
        }
    }

    private async showDefaultMessage(operation: Promise<OperationResult>): Promise<void> {
        try {
            const factory: DefaultMessagesSceneFactory = await this.contextResolver.resolve("DefaultMessagesSceneFactory");
            const scene = await factory({ operation, withProcessingPage: false });
            await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.logger.error("Exception on showing payment message: ", e);
        }
    }

    private walletTypeToExternalWalletType(connectedWalletType: ConnectedWalletType): ExternalWalletType {
        switch (connectedWalletType) {
            case ConnectedWalletType.METAMASK:
                return ExternalWalletType.METAMASK;
            case ConnectedWalletType.COINBASE:
                return ExternalWalletType.COINBASE;
            default:
                throw new Error(`Unable to convert ${connectedWalletType} to external wallet type`);
        }
    }
}
